import logger from "../utils/logger";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import { checkIsAdminSync, checkStaffAccess } from "../helpers/helpers";
import { supabase } from "../supabaseClient";
import { LogoSvg } from "../utils/svg";
import SuggestionsModal from "./SuggestionsModal";
import { clearScrollPosition, clearClickedEventId } from "../utils/scrollPositions";
import useUnreadMessages from "../hooks/useUnreadMessages";
import UnreadBadge from "./UnreadBadge";

import UserIcon from "../assets/icons/Account.png";
import CalendarIcon from "../assets/icons/Planner.png";
import DealIcon from "../assets/icons/Discount.png";
import SpinAndWinIcon from "../assets/icons/Community ticket.png";
import RequestIcon from "../assets/icons/Communication.png";

import LanguageSwitcher from "./LanguageSwitcher";
import TiktokIcon from "../assets/icons/TikTok.png";
import InstagramIcon from "../assets/icons/Instagram.png";

const SECTION_ACCENT = {
  account: "#8B5CF6",
  inbox: "#38BDF8",
  events: "#E48EF5",
  deals: "#FDBC64",
  restaurants: "#F97316",
  "spin-and-win": "#F59E0B",
  community: "#22D3EE",
  housemates: "#F2565F",
  jobs: "#06B6D4",
  families: "#82D9AD",
  games: "#FB7185",
  admin: "#999BEC",
};

const SidebarOption = ({
  option,
  currentSlug,
  cities,
  customPages,
  activeDropdown,
  setActiveDropdown,
  subActiveDropdown,
  setSubActiveDropdown,
  setShowSidebar,
}) => {
  const [hovering, setHovering] = useState(false);
  const accentColor = SECTION_ACCENT[option.id] || "#8B5CF6";

  if (option.showCitiesDropdown) {
    const isActive = option.activeLink === currentSlug;
    const isExpanded = activeDropdown === option.id;

    return (
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className="relative"
      >
        {isActive && (
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] rounded-r-full transition-all duration-300"
            style={{ backgroundColor: accentColor, boxShadow: `0 0 8px ${accentColor}80` }}
          />
        )}
        <button
          onClick={() => {
            const next = activeDropdown === option.id ? null : option.id;
            setActiveDropdown(next);
            if (!next) setSubActiveDropdown(null);
          }}
          className={`!text-neutral-400 hover:!text-white transition-all flex gap-[10px] items-center justify-between p-[10px] rounded-xl text-[14px] w-full ${
            isActive
              ? "bg-white/[0.06] !text-white !font-semibold"
              : "hover:bg-white/[0.03]"
          }`}
        >
          <div className="flex items-center gap-[10px]">
            <div
              className="flex items-center justify-center w-[30px] h-[30px] rounded-lg transition-all shrink-0"
              style={{ backgroundColor: isActive ? `${accentColor}18` : "transparent" }}
            >
              {option.iconComponent ? (
                <span className="flex items-center justify-center w-[20px] h-[20px]" style={{ color: isActive ? accentColor : undefined }}>
                  {option.iconComponent}
                </span>
              ) : (
                <img
                  src={option.icon}
                  className="w-[20px] h-[20px]"
                  alt={option.name}
                  style={{
                    filter: isActive ? `drop-shadow(0 0 3px ${accentColor}60)` : "none",
                  }}
                />
              )}
            </div>
            <p className="text-[14px]">{option.name}</p>
          </div>
          <Icon
            icon={isExpanded ? "heroicons:chevron-up" : "heroicons:chevron-down"}
            className="w-[14px] h-[14px] text-neutral-600 transition-transform duration-300"
          />
        </button>

        <div
          className="pl-[12px] overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: isExpanded
              ? `${42 + 42 + (subActiveDropdown === "locations" ? cities.length * 42 : 0) + (customPages.length > 0 ? 42 + (subActiveDropdown === "customPages" ? customPages.length * 42 : 0) : 0)}px`
              : "0px",
            opacity: isExpanded ? 1 : 0,
          }}
        >
          <div className="py-[4px] space-y-[2px] border-l-[2px] border-white/[0.04] ml-[20px]">
            <Link
              to={window.location.pathname.includes("admin") ? "/admin" : "/events?city=all_cities"}
              onClick={() => {
                setShowSidebar(false);
                try { clearScrollPosition("home-page"); clearClickedEventId(); } catch (e) {}
              }}
              className="py-[6px] px-[10px] flex items-center gap-[8px] rounded-lg text-[13px] text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-all"
            >
              <p>All Events</p>
            </Link>

            <button
              onClick={() => setSubActiveDropdown(subActiveDropdown === "locations" ? null : "locations")}
              className="py-[6px] px-[10px] flex items-center justify-between gap-[8px] rounded-lg text-[13px] text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-all w-full"
            >
              <p>Locations</p>
              <Icon
                icon={subActiveDropdown === "locations" ? "heroicons:chevron-up" : "heroicons:chevron-down"}
                className="w-[11px] h-[11px] text-neutral-600 transition-transform duration-300"
              />
            </button>
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: subActiveDropdown === "locations" ? `${cities.length * 42}px` : "0px",
                opacity: subActiveDropdown === "locations" ? 1 : 0,
              }}
            >
              <div className="pl-[12px] space-y-[2px] border-l-[2px] border-white/[0.04]">
                {cities.map((city) => (
                  <Link
                    onClick={() => {
                      setShowSidebar(false);
                      try { clearScrollPosition("home-page"); clearClickedEventId(); } catch (e) {}
                    }}
                    key={city.id}
                    to={window.location.pathname.includes("admin") ? `/admin/events/${city.name}` : `/events?city=${city.name}`}
                    className="py-[6px] px-[10px] flex items-center gap-[8px] rounded-lg text-[13px] text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-all"
                  >
                    <p>{city.name}</p>
                  </Link>
                ))}
              </div>
            </div>

            {customPages.length > 0 && (
              <>
                <button
                  onClick={() => setSubActiveDropdown(subActiveDropdown === "customPages" ? null : "customPages")}
                  className="py-[6px] px-[10px] flex items-center justify-between gap-[8px] rounded-lg text-[13px] text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-all w-full"
                >
                  <p>Custom Pages</p>
                  <Icon
                    icon={subActiveDropdown === "customPages" ? "heroicons:chevron-up" : "heroicons:chevron-down"}
                    className="w-[11px] h-[11px] text-neutral-600 transition-transform duration-300"
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{
                    maxHeight: subActiveDropdown === "customPages" ? `${customPages.length * 42}px` : "0px",
                    opacity: subActiveDropdown === "customPages" ? 1 : 0,
                  }}
                >
                  <div className="pl-[12px] space-y-[2px] border-l-[2px] border-white/[0.04]">
                    {customPages.map((page) => (
                      <Link
                        onClick={() => {
                          setShowSidebar(false);
                        }}
                        key={page.id}
                        to={`/page/${page.slug}`}
                        className="py-[6px] px-[10px] flex items-center gap-[8px] rounded-lg text-[13px] text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-all"
                      >
                        <p>{page.name}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isActive = option.activeLink === currentSlug;

  return (
    <div className="relative">
      {isActive && option.id !== "instagram" && option.id !== "tiktok" && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] rounded-r-full transition-all duration-300"
          style={{ backgroundColor: accentColor, boxShadow: `0 0 8px ${accentColor}80` }}
        />
      )}
      <Link
        to={option.link}
        key={option.id}
        target={option.target}
        className={`!text-neutral-400 hover:!text-white transition-all flex gap-[10px] items-center p-[10px] rounded-xl text-[14px] ${
          isActive
            ? "bg-white/[0.06] !text-white !font-semibold"
            : "hover:bg-white/[0.03]"
        }`}
      >
        <div
          className="flex items-center justify-center w-[30px] h-[30px] rounded-lg transition-all shrink-0"
          style={{ backgroundColor: isActive ? `${accentColor}18` : "transparent" }}
        >
          {option.iconComponent ? (
            <span className="flex items-center justify-center w-[20px] h-[20px]" style={{ color: isActive ? accentColor : undefined }}>
              {option.iconComponent}
            </span>
          ) : (
            <img
              src={option.icon}
              className="w-[20px] h-[20px]"
              alt={option.name}
              style={{
                filter: isActive ? `drop-shadow(0 0 3px ${accentColor}60)` : "none",
              }}
            />
          )}
        </div>
        <p className="text-[14px]">{option.name}</p>
      </Link>
    </div>
  );
};

const Sidebar = ({ language, setLanguage, isMobile, onClose = () => {} }) => {
  const currentSlug = window.location.href.split("?")[0].split("/").pop();
  const user = useSelector(selectUser);
  const [cities, setCities] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [subActiveDropdown, setSubActiveDropdown] = useState(null);
  const [customPages, setCustomPages] = useState([]);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [isPromoter, setIsPromoter] = useState(false);
  const [isUGC, setIsUGC] = useState(false);
  const [hasSharedForms, setHasSharedForms] = useState(false);

  const { unreadCount } = useUnreadMessages();

  const sidebarOptions = [
    ...(user.id
      ? [
        {
          id: "account",
          icon: UserIcon,
          name: "Account",
          link: "/profile/me",
          activeLink: "me",
        },
        {
          id: "inbox",
          iconComponent: (
            <span className="relative">
              <Icon icon="heroicons:chat-bubble-left-right" className="w-[22px] h-[22px]" />
              {unreadCount > 0 && <UnreadBadge count={unreadCount} compact />}
            </span>
          ),
          name: unreadCount > 0 ? `Inbox (${unreadCount})` : "Inbox",
          link: "/inbox",
          activeLink: "inbox",
        },
        ...(hasSharedForms
          ? [{
              id: "shared-forms",
              iconComponent: <Icon icon="heroicons:document-text" className="w-[22px] h-[22px]" />,
              name: "Forms",
              link: "/my-forms",
              activeLink: "my-forms",
            }]
          : []),
      ]
      : [{
          id: "login",
          icon: UserIcon,
          name: "Log in",
          link: "/email-login",
          activeLink: "email-login",
        }]),
    {
      id: "events",
      icon: CalendarIcon,
      name: "Events",
      link: "/",
      activeLink: "",
      showCitiesDropdown: true,
    },
    {
      id: "deals",
      icon: DealIcon,
      name: "Deals",
      link: "/lifestyle",
      activeLink: "lifestyle",
    },
    {
      id: "deal-requests",
      iconComponent: <Icon icon="heroicons:arrow-trending-up" className="w-[22px] h-[22px]" />,
      name: "Deals we want",
      link: "/deal-requests",
      activeLink: "deal-requests",
    },
    // Hide restaurants until live with active restaurants
    // {
    //   id: "restaurants",
    //   iconComponent: <Icon icon="mdi:silverware-fork-knife" className="w-[22px] h-[22px]" />,
    //   name: "Restaurants",
    //   link: "/restaurants",
    //   activeLink: "restaurants",
    // },
    {
      id: "spin-and-win",
      icon: SpinAndWinIcon,
      name: "Win tickets",
      link: "/spin-and-win",
      activeLink: "spin-and-win",
    },
    {
      id: "community",
      icon: RequestIcon,
      name: "Community",
      link: "/community-chats",
      activeLink: "community-chats",
    },
    {
      id: "housemates",
      iconComponent: <Icon icon="lucide:home" className="w-[22px] h-[22px]" />,
      name: "Housemates",
      link: "/housemates",
      activeLink: "housemates",
    },
    {
      id: "jobs",
      iconComponent: <Icon icon="lucide:briefcase" className="w-[22px] h-[22px]" />,
      name: "Jobs",
      link: "/jobs",
      activeLink: "jobs",
    },
    {
      id: "games",
      iconComponent: <Icon icon="lucide:gamepad-2" className="w-[22px] h-[22px]" />,
      name: "Games",
      link: "/games",
      activeLink: "games",
    },
    ...(isAdmin
      ? [{
        id: "families",
        iconComponent: <Icon icon="lucide:users" className="w-[22px] h-[22px]" />,
        name: "Families",
        link: "/families",
        activeLink: "families",
      }]
      : []),
    ...(isPartner
      ? [{ id: "partner-dashboard", icon: UserIcon, name: "Partner dashboard", link: "/partner-dashboard", activeLink: "partner-dashboard" }]
      : []),
    ...(isPromoter
      ? [{ id: "promoter-dashboard", icon: UserIcon, name: "Promoter Dashboard", link: "/team-member", activeLink: "team-member" }]
      : []),
    ...(isUGC
      ? [{ id: "ugc-dashboard", iconComponent: <Icon icon="heroicons:video-camera" className="w-[22px] h-[22px]" />, name: "UGC Dashboard", link: "/profile/ugc", activeLink: "ugc" }]
      : []),
    ...(isAdmin || isStaff
      ? [
        {
          id: "admin",
          icon: UserIcon,
          name: "Admin",
          link: "/admin",
          activeLink: "admin",
        },
      ]
      : []),
  ];

  const bottomOptions = [
    {
      id: "instagram",
      icon: InstagramIcon,
      name: "Instagram",
      link: "https://www.instagram.com/22night.worldwide/",
      activeLink: "instagram",
      target: "_blank",
    },
    {
      id: "tiktok",
      icon: TiktokIcon,
      name: "Tiktok",
      link: "https://www.tiktok.com/@22night.worldwide",
    },
  ];

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) { setIsAdmin(false); return; }
      try { setIsAdmin(await checkIsAdminSync()); } catch (error) { logger.error('Error checking admin status:', error); setIsAdmin(false); }
    };
    checkAdminStatus();
  }, [user?.id]);

  useEffect(() => {
    const checkStaff = async () => {
      if (!user?.id) { setIsStaff(false); return; }
      try {
        const access = await checkStaffAccess();
        setIsStaff(access.hasAccess && !access.isFullAdmin);
      } catch { setIsStaff(false); }
    };
    checkStaff();
  }, [user?.id]);

  useEffect(() => {
    const checkPartner = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const authId = auth?.user?.id;
        if (!authId) { setIsPartner(false); return; }
        const { data, error } = await supabase.from("User").select("id, partner_id").eq("auth_user_id", authId).maybeSingle();
        if (error) throw error;
        setIsPartner(!!data?.partner_id);
      } catch (e) { setIsPartner(false); }
    };
    checkPartner();
  }, [user?.id]);

  useEffect(() => {
    const checkSharedForms = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setHasSharedForms(false); return; }
        const { data: dbUser } = await supabase.from("User").select("id").eq("auth_user_id", auth.user.id).maybeSingle();
        if (!dbUser?.id) { setHasSharedForms(false); return; }
        const { data } = await supabase.from("FormShares").select("id").eq("user_id", dbUser.id).limit(1).maybeSingle();
        setHasSharedForms(!!data);
      } catch (e) { setHasSharedForms(false); }
    };
    checkSharedForms();
  }, [user?.id]);

  useEffect(() => {
    const checkPromoter = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setIsPromoter(false); return; }
        const { data } = await supabase.from("promoters").select("id").eq("user_id", auth.user.id).eq("status", "active").maybeSingle();
        setIsPromoter(!!data);
      } catch (e) { setIsPromoter(false); }
    };
    checkPromoter();
  }, [user?.id]);

  useEffect(() => {
    const checkUGC = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setIsUGC(false); return; }
        const authId = auth.user.id;

        const { data: dbUser } = await supabase.from("User")
          .select("id").eq("auth_user_id", authId).maybeSingle();
        const numericUserId = dbUser?.id;

        let data = null;
        if (numericUserId != null) {
          ({ data } = await supabase.from("ambassadors")
            .select("id").eq("user_id", numericUserId)
            .eq("is_ugc_partner", true).maybeSingle());
        }

        if (!data && auth.user.email) {
          const { data: emailMatch } = await supabase.from("ambassadors")
            .select("id").ilike("ugc_email", auth.user.email.trim())
            .eq("is_ugc_partner", true).maybeSingle();
          setIsUGC(!!emailMatch);
          return;
        }

        setIsUGC(!!data);
      } catch (e) { setIsUGC(false); }
    };
    checkUGC();
  }, [user?.id]);

  useEffect(() => {
    supabase.from("Events").select("city").gte("datetime", new Date().toISOString()).then((res) => {
      if (res.data) {
        const uniqueCities = [...new Set(res.data.map((event) => event.city))];
        supabase.from("City").select("*").in("name", uniqueCities).eq("archived", false).order("index", { ascending: true }).then((cityRes) => {
          setCities(cityRes.data || []);
        });
      }
    });
  }, []);

  useEffect(() => {
    supabase.from("custom_pages")
      .select("*")
      .eq("is_published", true)
      .order("created_at", { ascending: false })
      .then((res) => {
        if (res.data) setCustomPages(res.data);
      });
  }, []);

  return (
    <>
      <SuggestionsModal show={showSuggestionsModal} setShow={setShowSuggestionsModal} />
      <div
        className="top-0 z-[999] fixed flex flex-col justify-between px-[20px] py-[24px] max-lg:pb-[70px] lg:border-r lg:border-white/[0.05] lg:w-[250px] max-lg:w-full h-[100vh] overflow-y-auto"
        style={{ background: "rgba(11, 4, 21, 0.92)", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}
      >
        {/* Top accent line */}
        <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

        <div className="flex flex-col gap-[20px]">
          <div className="lg:hidden flex justify-between items-center">
            <button onClick={() => onClose()} className="text-white">
              <Icon icon="heroicons:x-mark" className="w-[22px] h-[22px]" />
            </button>
            <Link to="/" className="flex items-center">
              <LogoSvg />
            </Link>
            <div className="w-[22px]" />
          </div>
          <Link to="/" className="max-lg:hidden flex items-center px-[8px] py-[12px] h-[56px]">
            <LogoSvg />
          </Link>
          <div className="flex flex-col gap-[4px]">
            {sidebarOptions.map((option) => (
              <SidebarOption
                key={option.id}
                option={option}
                currentSlug={currentSlug}
                cities={cities}
                customPages={customPages}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
                subActiveDropdown={subActiveDropdown}
                setSubActiveDropdown={setSubActiveDropdown}
                setShowSidebar={onClose || (() => {})}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-[4px]">
          <LanguageSwitcher setLanguage={setLanguage} variant="sidebar" />
          {bottomOptions.map((option) => (
            <SidebarOption
              key={option.id}
              option={option}
              currentSlug={currentSlug}
              cities={cities}
              customPages={customPages}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
              subActiveDropdown={subActiveDropdown}
              setSubActiveDropdown={setSubActiveDropdown}
            />
          ))}
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-[20px] pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(11,4,21,0.92), transparent)" }}
        />
      </div>
    </>
  );
};

export default Sidebar;
